import { Document } from "@models";
import { RegulatoryTypeEnum } from "@types";
import { useNavigate } from "react-router-dom";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { buildDocumentRoute, fillRouteWithSlugs } from "src/utils/navigation";
import { useDeleteDocumentMutation } from "./mutations";

type UseDeleteDocumentActionArgs = {
  deviceId?: string;
  documents: Document[];
  orgId: string;
  queryDeviceIds?: string[];
};

const hasLiveVersion = (document: Document) =>
  document.versions.some((version) => version.revision !== null);

export const useDeleteDocumentAction = ({
  deviceId,
  documents,
  orgId,
  queryDeviceIds,
}: UseDeleteDocumentActionArgs) => {
  const navigate = useNavigate();
  const deleteDocumentMutation = useDeleteDocumentMutation();

  const canDeleteDocument = (document: Document) => !hasLiveVersion(document);

  const getDeleteDocumentTooltipTitle = (document: Document) =>
    canDeleteDocument(document)
      ? undefined
      : "Documents with live revisions cannot be deleted";

  const deleteDocument = async ({
    document,
    navigateOnDelete,
    suppressErrorSnackbar,
  }: {
    document: Document;
    navigateOnDelete: boolean;
    suppressErrorSnackbar?: boolean;
  }) => {
    const resolvedDeviceId = document.deviceId || deviceId || "";
    const documentQueryDeviceIds = document.deviceId
      ? [document.deviceId]
      : queryDeviceIds;

    await deleteDocumentMutation.mutateAsync({
      docId: document.id,
      deviceId: resolvedDeviceId,
      orgId,
      queryDeviceIds: documentQueryDeviceIds,
      suppressErrorSnackbar,
    });

    if (!navigateOnDelete || !resolvedDeviceId) {
      return;
    }

    const remainingDocuments = documents.filter((item) => item.id !== document.id);
    const sameTemplateDocuments = remainingDocuments.filter(
      (item) => item.templateKey === document.templateKey
    );
    const nextDocument =
      document.regulatoryType === RegulatoryTypeEnum.RCD
        ? sameTemplateDocuments[0]
        : undefined;
    const nextDocumentDeviceId =
      nextDocument?.deviceId || deviceId || resolvedDeviceId;

    if (nextDocument && nextDocumentDeviceId) {
      navigate(
        buildDocumentRoute({
          documentId: nextDocument.id,
          deviceId: nextDocumentDeviceId,
          orgId,
          templateKey: nextDocument.templateKey,
        }),
        { replace: true }
      );
      return;
    }

    navigate(
      fillRouteWithSlugs(ROUTES.QMS, {
        [ROUTE_SLUGS.DEVICE_ID]: resolvedDeviceId,
        [ROUTE_SLUGS.ORG_ID]: orgId,
      }),
      { replace: true }
    );
  };

  return {
    canDeleteDocument,
    deleteDocument,
    getDeleteDocumentTooltipTitle,
    isDeleteDocumentPending: deleteDocumentMutation.isPending,
  };
};
